import React, { useState, useEffect } from 'react';
import { useTriageStore } from '../lib/stores/triageStore';
import { 
  NC_STATUS_QUESTION, 
  getQuestionsForBranch, 
  determineBranch,
  getQuestionCountForBranch 
} from '../lib/questionnaireLogic';

const BranchingQuestionnaireForm = ({ onSubmit, onBack }) => {
  const { 
    ncStatus, 
    branchAnswers, 
    currentBranch, 
    currentQuestionIndex,
    setNcStatus, 
    setBranchAnswer, 
    setCurrentBranch, 
    setCurrentQuestionIndex,
    isNcStatusComplete,
    isComplete
  } = useTriageStore();

  const [currentStep, setCurrentStep] = useState('nc_status'); // 'nc_status' or 'branch_questions'

  // Initialize branch when NC status is set
  useEffect(() => {
    if (ncStatus && !currentBranch) {
      const branch = determineBranch(ncStatus);
      setCurrentBranch(branch);
      setCurrentStep('branch_questions');
    }
  }, [ncStatus, currentBranch, setCurrentBranch]);

  const handleNcStatusSelect = (value) => {
    setNcStatus(value);
  };

  const handleBranchAnswerSelect = (questionId, value) => {
    setBranchAnswer(questionId, value);
    
    // Auto-advance to next question if not the last one
    const questions = getQuestionsForBranch(currentBranch);
    const currentIndex = questions.findIndex(q => q.id === questionId);
    
    if (currentIndex < questions.length - 1) {
      setTimeout(() => {
        setCurrentQuestionIndex(currentIndex + 1);
      }, 300);
    }
  };

  const handleNext = () => {
    if (currentStep === 'nc_status' && isNcStatusComplete()) {
      setCurrentStep('branch_questions');
    } else if (currentStep === 'branch_questions') {
      const questions = getQuestionsForBranch(currentBranch);
      if (currentQuestionIndex < questions.length - 1) {
        setCurrentQuestionIndex(currentQuestionIndex + 1);
      }
    }
  };

  const handlePrevious = () => {
    if (currentStep === 'branch_questions') {
      if (currentQuestionIndex > 0) {
        setCurrentQuestionIndex(currentQuestionIndex - 1);
      } else {
        setCurrentStep('nc_status');
      }
    }
  };

  const handleSubmit = () => {
    if (isComplete()) {
      onSubmit({
        ncStatus,
        branchAnswers,
        currentBranch
      });
    }
  };

  const getProgressPercentage = () => {
    if (currentStep === 'nc_status') {
      return 10; // NC status is 10% of total
    } else {
      const totalQuestions = getQuestionCountForBranch(currentBranch);
      const ncStatusProgress = 10;
      const branchProgress = ((currentQuestionIndex + 1) / totalQuestions) * 90;
      return ncStatusProgress + branchProgress;
    }
  };

  const getCurrentQuestion = () => {
    if (currentStep === 'nc_status') {
      return NC_STATUS_QUESTION;
    } else {
      const questions = getQuestionsForBranch(currentBranch);
      return questions[currentQuestionIndex];
    }
  };

  const getCurrentAnswer = () => {
    if (currentStep === 'nc_status') {
      return ncStatus;
    } else {
      const question = getCurrentQuestion();
      return branchAnswers[question?.id];
    }
  };

  const canProceed = () => {
    if (currentStep === 'nc_status') {
      return isNcStatusComplete();
    } else {
      const question = getCurrentQuestion();
      return branchAnswers[question?.id] !== undefined;
    }
  };

  const getStepTitle = () => {
    if (currentStep === 'nc_status') {
      return "Step 1: National Curriculum Level";
    } else {
      const questions = getQuestionsForBranch(currentBranch);
      return `Step 2: ${currentBranch === 'PLAIN_LANGUAGE' ? 'Learning Profile' : 'Support Needs'} (${currentQuestionIndex + 1} of ${questions.length})`;
    }
  };

  const getStepDescription = () => {
    if (currentStep === 'nc_status') {
      return "First, let's understand where this pupil is working in relation to the National Curriculum.";
    } else {
      return currentBranch === 'PLAIN_LANGUAGE' 
        ? "Now let's understand how this pupil learns and communicates."
        : "Now let's identify the specific support needs for this pupil.";
    }
  };

  const currentQuestion = getCurrentQuestion();
  const currentAnswer = getCurrentAnswer();

  if (!currentQuestion) {
    return (
      <div className="max-w-4xl mx-auto">
        <div className="bg-red-50 border border-red-200 rounded-lg p-6">
          <h3 className="text-sm font-medium text-red-800">Error</h3>
          <p className="mt-2 text-sm text-red-700">Unable to load questionnaire. Please try again.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto">
      {/* Progress Bar */}
      <div className="mb-8">
        <div className="flex items-center justify-between mb-2">
          <span className="text-sm font-medium text-gray-700">
            {getStepTitle()}
          </span>
          <span className="text-sm text-gray-500">
            {Math.round(getProgressPercentage())}% Complete
          </span>
        </div>
        <div className="w-full bg-gray-200 rounded-full h-2">
          <div
            className="bg-indigo-600 h-2 rounded-full transition-all duration-300"
            style={{ width: `${getProgressPercentage()}%` }}
          ></div>
        </div>
      </div>

      {/* Step Description */}
      <div className="mb-6">
        <p className="text-gray-600 text-lg">
          {getStepDescription()}
        </p>
      </div>

      {/* Question */}
      <div className="bg-white rounded-lg shadow-lg p-8 mb-6">
        <div className="mb-6">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">
            {currentQuestion.text}
          </h2>
          <p className="text-sm text-gray-600 mb-3">
            Choose the description that best fits this pupil's current level.
          </p>
        </div>

        {/* Answer Options */}
        <div className="space-y-4">
          {currentQuestion.options.map((option) => (
            <div
              key={option.value}
              className={`p-4 border-2 rounded-lg cursor-pointer transition-all duration-200 ${
                currentAnswer === option.value
                  ? 'border-indigo-500 bg-indigo-50'
                  : 'border-gray-200 hover:border-gray-300'
              }`}
              onClick={() => {
                if (currentStep === 'nc_status') {
                  handleNcStatusSelect(option.value);
                } else {
                  handleBranchAnswerSelect(currentQuestion.id, option.value);
                }
              }}
            >
              <div className="flex items-center">
                <div className={`w-5 h-5 rounded-full border-2 mr-4 ${
                  currentAnswer === option.value
                    ? 'border-indigo-500 bg-indigo-500'
                    : 'border-gray-300'
                }`}>
                  {currentAnswer === option.value && (
                    <div className="w-2 h-2 bg-white rounded-full m-0.5"></div>
                  )}
                </div>
                <div className="flex-1">
                  <div className="font-medium text-gray-900">{option.label}</div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Navigation */}
      <div className="flex justify-between items-center">
        <button
          onClick={onBack}
          className="inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
        >
          <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
          </svg>
          Back
        </button>

        <div className="flex space-x-4">
          {currentStep === 'branch_questions' && currentQuestionIndex > 0 && (
            <button
              onClick={handlePrevious}
              className="inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
            >
              <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
              Previous
            </button>
          )}
          
          {!isComplete() && (
            <button
              onClick={handleNext}
              disabled={!canProceed()}
              className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Next
              <svg className="w-4 h-4 ml-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
            </button>
          )}
          
          {isComplete() && (
            <button
              onClick={handleSubmit}
              className="inline-flex items-center px-6 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500"
            >
              Complete Assessment
              <svg className="w-4 h-4 ml-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
            </button>
          )}
        </div>
      </div>

      {/* Question Navigation Dots */}
      {currentStep === 'branch_questions' && (
        <div className="flex justify-center mt-8">
          <div className="flex space-x-2">
            {getQuestionsForBranch(currentBranch).map((_, index) => (
              <button
                key={index}
                onClick={() => setCurrentQuestionIndex(index)}
                className={`w-3 h-3 rounded-full transition-colors duration-200 ${
                  index === currentQuestionIndex
                    ? 'bg-indigo-600'
                    : branchAnswers[getQuestionsForBranch(currentBranch)[index]?.id] !== undefined
                    ? 'bg-green-400'
                    : 'bg-gray-300'
                }`}
              />
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default BranchingQuestionnaireForm;
